/**
 * Outbox reducer: every send the sender has queued, and which of them the
 * outbox is showing.
 */

import { OutboxLogic } from './logic';
import type { OutboxRunProgress, OutboxSend } from './types';

export interface OutboxState {
  /** Every share in the outbox, newest first. */
  sends: OutboxSend[];
  /** Whether the stored outbox has been read yet. */
  isLoaded: boolean;
  isOpen: boolean;
  /** The send the outbox is showing, when the sender picked one. */
  activeSendId: string | null;
  /** Which sharing interstitials the outbox has shown this app session. */
  interstitialsShown: ReadonlySet<string>;
}

/** A change to one message of a send, by the message's place in it. */
type ItemChange =
  | 'CONFIRM'
  | 'SHARE_LATER'
  | 'MARK_SENT'
  | 'MARK_NOT_SENT'
  | 'REMOVE_ITEM';

/** A change to every message of a send at once. */
type SendChange = 'MARK_ALL_SENT' | 'MARK_ALL_NOT_SENT';

export type OutboxAction =
  // The stored outbox, read at launch. `openOn` is the topic a launch through
  // the sender's own leg link names (`OUTBOX_OPEN_PARAM`), if any.
  | { type: 'LOADED'; stored: OutboxSend[]; openOn: string | null }
  // The stored outbox again, after another tab changed it.
  | { type: 'RELOADED'; stored: OutboxSend[] }
  | { type: 'QUEUE_SEND'; send: OutboxSend }
  | { type: 'REMOVE_SEND'; sendId: string }
  // The message at `index` handed to the SMS app on the leg `legId`.
  | { type: 'OPEN_ITEM'; sendId: string; index: number; legId: string }
  | { type: ItemChange; sendId: string; index: number }
  | { type: SendChange; sendId: string }
  | { type: 'SET_RUN_PROGRESS'; sendId: string; runProgress: OutboxRunProgress }
  | { type: 'OPEN_OUTBOX'; topicId?: string }
  | { type: 'CLOSE_OUTBOX' }
  | { type: 'SHOW_SEND'; sendId: string }
  | { type: 'RECORD_INTERSTITIAL_SHOWN'; key: string };

type WithoutSendId<A> = A extends unknown ? Omit<A, 'sendId'> : never;

/** An action on one send, less the id of the send it applies to. */
export type OutboxSendChange = WithoutSendId<
  Extract<OutboxAction, { sendId: string }>
>;

const ITEM_CHANGES: Record<
  ItemChange,
  (send: OutboxSend, index: number) => OutboxSend
> = {
  CONFIRM: OutboxLogic.confirm,
  SHARE_LATER: OutboxLogic.shareLater,
  MARK_SENT: OutboxLogic.markSent,
  MARK_NOT_SENT: OutboxLogic.markNotSent,
  REMOVE_ITEM: OutboxLogic.remove,
};

const SEND_CHANGES: Record<SendChange, (send: OutboxSend) => OutboxSend> = {
  MARK_ALL_SENT: OutboxLogic.markAllSent,
  MARK_ALL_NOT_SENT: OutboxLogic.markAllNotSent,
};

/**
 * Actions that bring in what storage already holds. Everything else changes
 * the sends in a way that has to be stored.
 */
export function isFromStorage(action: OutboxAction): boolean {
  return action.type === 'LOADED' || action.type === 'RELOADED';
}

export function createEmptyOutboxState(): OutboxState {
  return {
    sends: [],
    isLoaded: false,
    isOpen: false,
    activeSendId: null,
    interstitialsShown: new Set(),
  };
}

/** The send the outbox shows: the one picked, else the default one. */
export function activeSendOf(state: OutboxState): OutboxSend | null {
  return (
    state.sends.find(send => send.id === state.activeSendId) ??
    OutboxLogic.defaultSend(state.sends)
  );
}

function withSend(
  state: OutboxState,
  sendId: string,
  change: (send: OutboxSend) => OutboxSend
): OutboxState {
  if (!state.sends.some(send => send.id === sendId)) return state;
  return {
    ...state,
    sends: state.sends.map(send => (send.id === sendId ? change(send) : send)),
  };
}

export function outboxReducer(
  state: OutboxState,
  action: OutboxAction
): OutboxState {
  switch (action.type) {
    case 'LOADED': {
      // A share queued before the stored outbox came back is newer than
      // anything stored for its topic.
      const sends = state.sends.reduce(
        (all, send) => OutboxLogic.withSendQueued(all, send),
        action.stored
      );
      // A send still waiting on "did it send?" means the sender left for
      // Messages and the app was closed behind them, so the outbox opens on it
      // to ask.
      const opening =
        (action.openOn && sends.find(send => send.topicId === action.openOn)) ||
        sends.find(send => OutboxLogic.awaitingConfirmIndex(send) >= 0);
      return {
        ...state,
        sends,
        isLoaded: true,
        ...(opening ? { activeSendId: opening.id, isOpen: true } : {}),
      };
    }
    case 'RELOADED':
      return { ...state, sends: action.stored };
    case 'QUEUE_SEND':
      return {
        ...state,
        sends: OutboxLogic.withSendQueued(state.sends, action.send),
        activeSendId: action.send.id,
      };
    case 'REMOVE_SEND': {
      const sends = state.sends.filter(send => send.id !== action.sendId);
      return {
        ...state,
        sends,
        activeSendId: OutboxLogic.defaultSend(sends)?.id ?? null,
      };
    }
    case 'OPEN_ITEM':
      return withSend(state, action.sendId, send =>
        OutboxLogic.open(
          OutboxLogic.withLeg(send, action.index, action.legId),
          action.index
        )
      );
    case 'CONFIRM':
    case 'SHARE_LATER':
    case 'MARK_SENT':
    case 'MARK_NOT_SENT':
    case 'REMOVE_ITEM': {
      const change = ITEM_CHANGES[action.type];
      return withSend(state, action.sendId, send => change(send, action.index));
    }
    case 'MARK_ALL_SENT':
    case 'MARK_ALL_NOT_SENT':
      return withSend(state, action.sendId, SEND_CHANGES[action.type]);
    case 'SET_RUN_PROGRESS':
      return withSend(state, action.sendId, send => ({
        ...send,
        runProgress: action.runProgress,
      }));
    case 'OPEN_OUTBOX': {
      const forTopic = action.topicId
        ? state.sends.find(send => send.topicId === action.topicId)
        : undefined;
      const send = forTopic ?? OutboxLogic.defaultSend(state.sends);
      return { ...state, activeSendId: send?.id ?? null, isOpen: true };
    }
    case 'CLOSE_OUTBOX':
      return { ...state, isOpen: false };
    case 'SHOW_SEND':
      return { ...state, activeSendId: action.sendId };
    case 'RECORD_INTERSTITIAL_SHOWN':
      return {
        ...state,
        interstitialsShown: new Set(state.interstitialsShown).add(action.key),
      };
    default:
      return state;
  }
}
